import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Clock, Inbox, PenLine, UserPlus } from 'lucide-react';
import type { QueueItem, QueueKind } from '@/hooks/useNeedsYouQueue';

const ICONS: Record<QueueKind, typeof AlertTriangle> = {
  compile_failed: AlertTriangle,
  stale_draft: Clock,
  review_request: Inbox,
  review_draft: PenLine,
  invite: UserPlus,
};

interface NeedsYouQueueProps {
  items: QueueItem[];
}

/** A short, mixed queue. Writing signals first, review chores after. */
const NeedsYouQueue = ({ items }: NeedsYouQueueProps) => {
  if (items.length === 0) return null;

  return (
    <section className="mb-12">
      <h2 className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-4">
        Needs you
      </h2>
      <ul className="rounded-xl border border-border/60 bg-card divide-y divide-border/60">
        {items.slice(0, 6).map((item) => {
          const Icon = ICONS[item.kind];
          return (
            <li key={item.id}>
              <Link
                to={item.href}
                className="group flex items-center gap-4 px-5 py-4 transition-colors hover:bg-accent/40"
              >
                <Icon
                  className={
                    item.kind === 'compile_failed'
                      ? 'h-4 w-4 shrink-0 text-destructive'
                      : 'h-4 w-4 shrink-0 text-muted-foreground'
                  }
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{item.title}</div>
                  <div className="truncate text-xs text-muted-foreground">{item.detail}</div>
                </div>
                <span className="hidden sm:inline-flex items-center text-xs text-muted-foreground group-hover:text-primary">
                  {item.actionLabel}
                  <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default NeedsYouQueue;
